import React, { Component, ReactNode } from 'react';
import { AppNotificationInterface, AppNotificationType, AppNotification } from '../../models/AppNotification';
import { store } from '../../app/store';
import { Unsubscribe } from '@reduxjs/toolkit';
import Toast from 'react-bootstrap/Toast';
import actions from '../../app/notification/actions';
import './NotificationManager.scss';
import UIfx from 'uifx';

enum NotificationMode {
  TOAST = 'toast',
  NOTIFICATION_API = 'notification_api',
}

interface NotificationManagerState {
  currentNotifications: AppNotificationInterface[];
}

interface DisplayNewNotificationsOptions {
  mode?: NotificationMode;
}

export class NotificationManager extends Component<any, NotificationManagerState> {
  private static readonly TOAST_DISPLAY_DURATION = 4000;

  // eslint-disable-next-line @typescript-eslint/no-var-requires
  sound = new UIfx(require('./quite-impressed.ogg'));
  storeUnsubscribe?: Unsubscribe;
  state: NotificationManagerState = {
    currentNotifications: [],
  };

  componentDidMount(): void {
    this.checkForNewNotification();
    this.storeUnsubscribe = store.subscribe(() => {
      this.checkForNewNotification();
    });
  }

  componentWillUnmount(): void {
    if (this.storeUnsubscribe) {
      this.storeUnsubscribe();
    }
  }

  checkForNewNotification(): void {
    const { currentNotifications } = this.state;
    const { list } = store.getState().notification;

    const newNotifications: AppNotificationInterface[] = [];
    const newToasts: AppNotificationInterface[] = [];
    list.forEach(newNotification => {
      let isNew = true;
      for (const currentNotification of currentNotifications) {
        if (AppNotification.match(currentNotification, newNotification)) {
          isNew = false;
          break;
        }
      }
      if (isNew) {
        if (newNotification.alwaysToast) {
          newToasts.push(newNotification);
        } else {
          newNotifications.push(newNotification);
        }
      }
    });

    if (newNotifications.length > 0) {
      this.displayNewNotifications(newNotifications);
    }

    if (newToasts.length > 0) {
      this.displayNewNotifications(newToasts, { mode: NotificationMode.TOAST });
    }
  }

  private getNotificationMode(): NotificationMode {
    if (!('Notification' in window)) {
      return NotificationMode.TOAST;
    } else if (Notification.permission === 'granted') {
      return NotificationMode.NOTIFICATION_API;
    } else {
      return NotificationMode.TOAST;
    }
  }

  displayNewNotifications(notifications: AppNotificationInterface[], { mode }: DisplayNewNotificationsOptions = {}): void {
    if (!mode) {
      mode = this.getNotificationMode();
    }

    switch (mode) {
      case NotificationMode.NOTIFICATION_API:
        notifications.forEach(notification => new Notification(
          `MijnTOPP: ${notification.title ? notification.title : ''}`,
          {
            body: notification.message,
            silent: notification.silent,
            icon: '/assets/logo.png',
          }
        ));
        store.dispatch(actions.removeList(notifications));
        break;
      case NotificationMode.TOAST:
      default:
        this.setState({ currentNotifications: [...this.state.currentNotifications, ...notifications] });
        // Play a sound for the new notifications if there is one that requires sound
        for (const notification of notifications) {
          if (!notification.silent) {
            this.sound.play();
            break;
          }
        }
        break;
    }
  }

  handleCloseToast = (notification: AppNotificationInterface): void => {
    const newCurrentNotifications: AppNotificationInterface[] = [];
    this.state.currentNotifications.forEach(currentNotification => {
      if (!AppNotification.match(notification, currentNotification)) {
        newCurrentNotifications.push(currentNotification);
      }
    });
    this.setState({ currentNotifications: newCurrentNotifications });
    store.dispatch(actions.remove(notification));
  };

  render(): ReactNode {
    const { currentNotifications } = this.state;

    return (
      <div className="notification-manager">
        {
          currentNotifications.map((notification) => {
            let toastClassType = '';
            switch (notification.type) {
              case AppNotificationType.ERROR:
                toastClassType = 'toast--error';
                break;
              case AppNotificationType.SUCCESS:
                toastClassType = 'toast--success';
                break;
            }

            let displayDuration = NotificationManager.TOAST_DISPLAY_DURATION;
            if (notification.duration) {
              displayDuration = notification.duration;
            }

            return (
              <Toast
                key={notification.time}
                className={`notification-manager__toast ${toastClassType}`}
                onClose={() => this.handleCloseToast(notification)}
                show={true}
                delay={displayDuration}
                autohide
              >
                {
                  notification.title &&
                  <Toast.Header>
                    <strong className="mr-auto">{notification.title}</strong>
                  </Toast.Header>
                }
                <Toast.Body>{notification.message}</Toast.Body>
              </Toast>
            );
          })
        }
      </div>
    );
  }
}
